/**
 * AccompagnementsMairiePage — item 9.3, côté mairie.
 *
 * ─── DEUX GESTES, DEUX ONGLETS ──────────────────────────────────────────────
 *
 * PARAMÉTRER : la mairie déclare ce qu'elle propose et les pièces qu'elle veut.
 * TRAITER : elle lit les demandes reçues, accepte ou refuse.
 *
 * Ce n'est pas un module de communication. La mairie ne fabrique aucune affiche
 * ici : elle propose un accompagnement, et une association le demande. Toute la
 * valeur est dans le formulaire paramétrable — le reste est du terrain qu'on
 * n'a pas encore arpenté, et qu'on n'invente donc pas.
 */
import { useCallback, useEffect, useState } from 'react';
import { HandHeart, Plus, Trash2, Inbox, Check, X, Paperclip, CalendarDays } from 'lucide-react';
import { api, ApiException, type ServiceMairieDTO, type DemandeServiceDTO } from '@/lib/api';
import { AppHeader } from '@/components/AppHeader';
import { Card, Button, Field, Input, Alert, Badge, SectionEyebrow } from '@/components/ui';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';

const STATUT_LABEL: Record<string, string> = {
  brouillon: 'Brouillon chez le demandeur',
  soumise: 'À traiter',
  acceptee: 'Acceptée',
  refusee: 'Refusée',
  cloturee: 'Clôturée',
};

function fr(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

export function AccompagnementsMairiePage() {
  const { confirm, prompt, dialogElement } = useConfirmDialog();
  const [onglet, setOnglet] = useState<'demandes' | 'parametrage'>('demandes');
  const [services, setServices] = useState<ServiceMairieDTO[] | null>(null);
  const [demandes, setDemandes] = useState<DemandeServiceDTO[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [nouveau, setNouveau] = useState<{
    libelle: string; description: string;
    typeOffre: 'accompagnement' | 'evenement';
    organisateurNom: string; lieu: string; dateDebut: string; placesMax: string;
  } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const charger = useCallback(async () => {
    setErreur(null);
    try {
      const [s, d] = await Promise.all([
        api.associations.services.parametrage(),
        api.associations.services.demandesRecues(),
      ]);
      setServices(s.services);
      setDemandes(d.demandes);
    } catch (e) {
      setErreur(e instanceof ApiException ? e.payload.error : 'Chargement impossible');
    }
  }, []);
  useEffect(() => { void charger(); }, [charger]);

  async function creerService() {
    if (!nouveau?.libelle.trim()) { setErreur('Un libellé est attendu.'); return; }
    try {
      await api.associations.services.creer({
        libelle: nouveau.libelle.trim(),
        description: nouveau.description.trim() || null,
        typeOffre: nouveau.typeOffre,
        // Le nom de l'organisateur est FIGÉ ici : une association renommée ne
        // doit pas réécrire l'affiche d'un événement passé.
        organisateurType: nouveau.organisateurNom.trim() ? 'association' : 'mairie',
        organisateurNom: nouveau.organisateurNom.trim() || null,
        lieu: nouveau.lieu.trim() || null,
        dateDebut: nouveau.typeOffre === 'evenement' ? (nouveau.dateDebut || null) : null,
        placesMax: nouveau.placesMax ? Number(nouveau.placesMax) : null,
      });
      setNouveau(null); await charger();
    } catch (e) {
      setErreur(e instanceof ApiException ? e.payload.error : 'Création impossible');
    }
  }

  async function ajouterPiece(s: ServiceMairieDTO) {
    const libelle = await prompt({
      title: `Pièce à demander — ${s.libelle}`,
      description: 'Ce que le demandeur devra déposer. Ex : « logo de l’association en haute définition ».',
      confirmLabel: 'Ajouter',
    });
    if (libelle === null || !libelle.trim()) return;
    try {
      await api.associations.services.ajouterPiece(s.id, { libelle: libelle.trim() });
      await charger();
    } catch (e) {
      setErreur(e instanceof ApiException ? e.payload.error : 'Ajout impossible');
    }
  }

  async function retirerPiece(id: string) {
    const ok = await confirm({
      title: 'Retirer cette pièce ?',
      description: 'Elle ne sera plus demandée aux prochains dossiers. Les fichiers déjà déposés sont conservés.',
      danger: true, confirmLabel: 'Retirer',
    });
    if (!ok) return;
    try { await api.associations.services.supprimerPiece(id); await charger(); }
    catch (e) { setErreur(e instanceof ApiException ? e.payload.error : 'Suppression impossible'); }
  }

  async function basculerActif(s: ServiceMairieDTO) {
    try {
      await api.associations.services.maj(s.id, {
        libelle: s.libelle, description: s.description, actif: !s.actif, ordre: s.ordre,
      });
      await charger();
    } catch (e) {
      setErreur(e instanceof ApiException ? e.payload.error : 'Modification impossible');
    }
  }

  async function traiter(d: DemandeServiceDTO, statut: 'acceptee' | 'refusee') {
    // Le motif est EXIGÉ au refus, côté base comme côté API : c'est ce que le
    // demandeur recevra, et un refus sans motif ne s'explique pas au téléphone.
    const texte = await prompt({
      title: statut === 'acceptee'
        ? `Accepter — ${d.serviceLibelle}` : `Refuser — ${d.serviceLibelle}`,
      description: statut === 'acceptee'
        ? 'Message au demandeur (facultatif) : ce que vous allez faire, quand, avec qui.'
        : 'Motif du refus — obligatoire. Il sera communiqué au demandeur.',
      multiline: true,
      confirmLabel: statut === 'acceptee' ? 'Accepter' : 'Refuser',
    });
    if (texte === null) return;
    if (statut === 'refusee' && !texte.trim()) { setErreur('Le motif du refus est obligatoire.'); return; }
    setBusy(d.id);
    try {
      await api.associations.services.traiter(d.id, statut === 'acceptee'
        ? { statut, reponse: texte.trim() || null }
        : { statut, motifRefus: texte.trim() });
      await charger();
    } catch (e) {
      setErreur(e instanceof ApiException ? e.payload.error : 'Traitement impossible');
    } finally { setBusy(null); }
  }

  async function ouvrirPiece(id: string) {
    try {
      const { url } = await api.associations.services.urlPiece(id);
      window.open(url, '_blank', 'noopener');
    } catch { setErreur('Pièce illisible.'); }
  }

  const aTraiter = (demandes ?? []).filter((d) => d.statut === 'soumise');

  return (
    <div className="min-h-screen text-ink">
      <AppHeader module="Association / Acteurs locaux" accent="asso" />
      <main id="main-content" className="mx-auto max-w-5xl px-4 py-6 sm:px-6 sm:py-8">
        <SectionEyebrow>Accompagnements</SectionEyebrow>
        <h1 className="mt-1 font-display text-xl font-semibold text-ink sm:text-2xl">
          Ce que la mairie propose
        </h1>
        <p className="mt-1 text-sm text-ink-subtle">
          Déclarez un accompagnement et les pièces que vous voulez recevoir. Les
          associations et acteurs locaux le verront dans leur espace et pourront
          le demander.
        </p>

        {erreur && <Alert variant="error" className="mt-4">{erreur}</Alert>}

        <div className="mt-5 flex gap-2">
          <Button size="sm" variant={onglet === 'demandes' ? 'primary' : 'ghost'}
            onClick={() => setOnglet('demandes')}>
            <Inbox className="h-4 w-4" aria-hidden="true" />
            Demandes reçues{aTraiter.length > 0 ? ` (${aTraiter.length})` : ''}
          </Button>
          <Button size="sm" variant={onglet === 'parametrage' ? 'primary' : 'ghost'}
            onClick={() => setOnglet('parametrage')}>
            <HandHeart className="h-4 w-4" aria-hidden="true" /> Paramétrage
          </Button>
        </div>

        {onglet === 'demandes' && (
          <div className="mt-4 space-y-3">
            {demandes === null && <p className="text-sm text-ink-subtle">Chargement…</p>}
            {demandes?.length === 0 && (
              <Card className="p-5 text-sm text-ink-subtle">
                Aucune demande pour l’instant. Vérifiez qu’un accompagnement est
                bien déclaré et actif dans le paramétrage.
              </Card>
            )}
            {demandes?.map((d) => (
              <Card key={d.id} className="p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <strong className="text-ink">{d.serviceLibelle}</strong>
                  <Badge size="sm" variant={d.statut === 'soumise' ? 'urgent' : 'neutral'}>
                    {STATUT_LABEL[d.statut] ?? d.statut}
                  </Badge>
                  <span className="ml-auto text-xs text-ink-muted">
                    Déposée le {fr(d.soumisAt ?? d.createdAt)}
                  </span>
                </div>
                <p className="mt-1 text-sm text-ink-muted">
                  {d.beneficiaireNom ?? d.demandeurNom ?? 'Demandeur inconnu'}
                </p>
                {d.message && <p className="mt-2 text-sm text-ink">{d.message}</p>}

                {d.pieces.length > 0 && (
                  <ul className="mt-2 space-y-0.5">
                    {d.pieces.map((p) => (
                      <li key={p.id}>
                        <button type="button" onClick={() => void ouvrirPiece(p.id)}
                          className="flex items-center gap-1.5 text-xs text-tech-fg hover:underline">
                          <Paperclip className="h-3.5 w-3.5" aria-hidden="true" />
                          {p.nomFichier}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}

                {d.motifRefus && (
                  <p className="mt-2 rounded-r-lg bg-urgent-tint/50 p-2 text-xs text-urgent">
                    Motif du refus : {d.motifRefus}
                  </p>
                )}
                {d.reponse && (
                  <p className="mt-2 text-xs text-ink-muted">Réponse : {d.reponse}</p>
                )}

                {d.statut === 'soumise' && (
                  <div className="mt-3 flex flex-wrap gap-2 border-t border-border pt-3">
                    <Button size="sm" variant="primary" disabled={busy === d.id}
                      onClick={() => void traiter(d, 'acceptee')}>
                      <Check className="h-4 w-4" aria-hidden="true" /> Accepter
                    </Button>
                    <Button size="sm" variant="ghost" className="text-urgent-fg" disabled={busy === d.id}
                      onClick={() => void traiter(d, 'refusee')}>
                      <X className="h-4 w-4" aria-hidden="true" /> Refuser
                    </Button>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}

        {onglet === 'parametrage' && (
          <div className="mt-4 space-y-3">
            {nouveau === null ? (
              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={() => setNouveau({
                  libelle: '', description: '', typeOffre: 'accompagnement',
                  organisateurNom: '', lieu: '', dateDebut: '', placesMax: '' })}>
                  <Plus className="h-4 w-4" aria-hidden="true" /> Nouvel accompagnement
                </Button>
                {/* 9.5 — un événement de tiers : même formulaire, plus une date.
                    C'est la mairie qui le saisit en nommant l'organisateur ; la
                    création par l'association elle-même demanderait un circuit
                    de modération qu'on n'a pas cadré. */}
                <Button size="sm" variant="secondary" onClick={() => setNouveau({
                  libelle: '', description: '', typeOffre: 'evenement',
                  organisateurNom: '', lieu: '', dateDebut: '', placesMax: '' })}>
                  <CalendarDays className="h-4 w-4" aria-hidden="true" /> Nouvel événement
                </Button>
              </div>
            ) : (
              <Card className="p-4">
                <div className="space-y-3">
                  <Field label="Libellé" htmlFor="s-lib">
                    <Input id="s-lib" value={nouveau.libelle}
                      placeholder="Ex : Accompagnement communication"
                      onChange={(e) => setNouveau({ ...nouveau, libelle: e.target.value })} />
                  </Field>
                  <Field label="Ce que vous proposez" htmlFor="s-desc"
                    hint="Affiché au demandeur avant qu’il dépose son dossier">
                    <Input id="s-desc" value={nouveau.description}
                      placeholder="Relecture d’affiche, relais sur les canaux de la commune…"
                      onChange={(e) => setNouveau({ ...nouveau, description: e.target.value })} />
                  </Field>
                  {nouveau.typeOffre === 'evenement' && (
                    <>
                      <Field label="Organisateur" htmlFor="s-orga"
                        hint="L’association ou l’acteur local. Vide = la mairie.">
                        <Input id="s-orga" value={nouveau.organisateurNom}
                          onChange={(e) => setNouveau({ ...nouveau, organisateurNom: e.target.value })} />
                      </Field>
                      <div className="flex flex-wrap gap-3">
                        <div className="flex-1"><Field label="Date" htmlFor="s-date">
                          <Input id="s-date" type="date" value={nouveau.dateDebut}
                            onChange={(e) => setNouveau({ ...nouveau, dateDebut: e.target.value })} />
                        </Field></div>
                        <div className="flex-1"><Field label="Lieu" htmlFor="s-lieu">
                          <Input id="s-lieu" value={nouveau.lieu}
                            onChange={(e) => setNouveau({ ...nouveau, lieu: e.target.value })} />
                        </Field></div>
                        <div className="flex-1"><Field label="Places" htmlFor="s-places" hint="Vide = illimité">
                          <Input id="s-places" type="number" min={1} value={nouveau.placesMax}
                            onChange={(e) => setNouveau({ ...nouveau, placesMax: e.target.value })} />
                        </Field></div>
                      </div>
                    </>
                  )}
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => void creerService()}>Créer</Button>
                    <Button size="sm" variant="ghost" onClick={() => setNouveau(null)}>Annuler</Button>
                  </div>
                </div>
              </Card>
            )}

            {services?.length === 0 && (
              <Card className="p-5 text-sm text-ink-subtle">
                Aucun accompagnement déclaré. Tant qu’il n’y en a pas, les
                associations n’ont rien à demander.
              </Card>
            )}

            {services?.map((s) => (
              <Card key={s.id} className="p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <strong className="text-ink">{s.libelle}</strong>
                  {s.typeOffre === 'evenement' && (
                    <Badge size="sm" variant="asso">
                      Événement{s.dateDebut ? ` · ${s.dateDebut.split('-').reverse().join('/')}` : ''}
                    </Badge>
                  )}
                  {s.placesMax != null && (
                    <Badge size="sm" variant={s.placesPrises >= s.placesMax ? 'urgent' : 'neutral'}>
                      {s.placesPrises} / {s.placesMax} places
                    </Badge>
                  )}
                  {!s.actif && <Badge size="sm" variant="neutral">Retiré de l’offre</Badge>}
                  <span className="ml-auto flex gap-2">
                    <Button size="sm" variant="ghost" onClick={() => void basculerActif(s)}>
                      {s.actif ? 'Retirer de l’offre' : 'Remettre'}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => void ajouterPiece(s)}>
                      <Plus className="h-4 w-4" aria-hidden="true" /> Pièce
                    </Button>
                  </span>
                </div>
                {s.description && <p className="mt-1 text-sm text-ink-muted">{s.description}</p>}

                {s.pieces.length === 0 ? (
                  <p className="mt-2 text-xs text-ink-subtle">
                    Aucune pièce demandée : le dossier se résumera au message du demandeur.
                  </p>
                ) : (
                  <ul className="mt-2 space-y-0.5">
                    {s.pieces.map((p) => (
                      <li key={p.id} className="flex items-center gap-2 text-sm text-ink">
                        <span>{p.libelle}</span>
                        {!p.obligatoire && (
                          <span className="text-xs text-ink-muted">— facultative</span>
                        )}
                        <Button size="sm" variant="ghost" className="ml-auto"
                          onClick={() => void retirerPiece(p.id)}
                          aria-label={`Retirer ${p.libelle}`}>
                          <Trash2 className="h-4 w-4" aria-hidden="true" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            ))}
          </div>
        )}
      </main>
      {dialogElement}
    </div>
  );
}
